import React, { useState } from 'react';
import { Heading } from '../catalyst/heading';
import { Select } from '../catalyst/select';
import { Input } from '../catalyst/input';
import { Button } from '../catalyst/button';
import {
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableHeader,
	TableCell,
} from '../catalyst/table';
import { OrderStatusBadge, PaymentStatusBadge, ProdigiStatusBadge, LobStatusBadge } from '../components/StatusBadge';
import { useOrders } from '../hooks/useOrders';
import type { OrderStatus, PaymentStatus, ProdigiStatus, LobStatus } from '../types/database';
import { Package } from 'lucide-react';
import ImageThumbnail from '../components/ImageThumbnail';
import ResourceLink from '../components/ResourceLink';

const ORDER_STATUSES: OrderStatus[] = [
	'DRAFT',
	'IN_REVIEW',
	'PENDING_PAYMENT',
	'PAID',
	'FULFILLING',
	'SHIPPED',
	'DELIVERED',
	'CANCELLED',
];

const STATUS_LABELS: Record<OrderStatus, string> = {
	DRAFT: 'Draft',
	IN_REVIEW: 'In Review',
	PENDING_PAYMENT: 'Pending Payment',
	PAID: 'Paid',
	FULFILLING: 'Fulfilling',
	SHIPPED: 'Shipped',
	DELIVERED: 'Delivered',
	CANCELLED: 'Cancelled',
};

const PAGE_SIZE = 20;

function getSlotText(slots: {slot_name: string; value: Record<string, unknown> | null}[], name: string): string {
	const slot = slots?.find(s => s.slot_name === name);
	if (!slot?.value) return '—';
	if ('name' in slot.value) return String(slot.value.name);
	if ('text' in slot.value) return String(slot.value.text);
	const keys = Object.keys(slot.value);
	if (keys.length === 1) return String(slot.value[keys[0]]);
	return '—';
}

export default function OrdersPage() {
	const [statusFilter, setStatusFilter] = useState<string>('');
	const [searchQuery, setSearchQuery] = useState('');
	const [offset, setOffset] = useState(0);
	const [dateRange, setDateRange] = useState<string>('');

	const { data: orders, loading, error, count } = useOrders({
		status: statusFilter || undefined,
		limit: PAGE_SIZE,
		offset,
		dateRange: dateRange || undefined,
	});

	const filteredOrders = searchQuery
		? orders.filter((o) => o.id.toLowerCase().includes(searchQuery.toLowerCase()))
		: orders;

	const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setStatusFilter(e.target.value);
		setOffset(0);
	};

	const handleDateRangeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setDateRange(e.target.value);
		setOffset(0);
	};

	const totalCount = count ?? 0;
	const showingStart = totalCount > 0 ? offset + 1 : 0;
	const showingEnd = offset + orders.length;

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-center gap-3">
					<Package className="h-6 w-6 text-zinc-500" />
					<Heading>Orders</Heading>
				</div>
			</div>

			<div className="flex flex-wrap items-center gap-4 mb-6">
				<div className="w-48">
					<Select value={statusFilter} onChange={handleStatusChange}>
						<option value="">All Statuses</option>
						{ORDER_STATUSES.map((s) => (
							<option key={s} value={s}>
								{STATUS_LABELS[s]}
							</option>
						))}
					</Select>
				</div>
				<div className="w-64">
					<Input
						type="text"
						placeholder="Search by order ID..."
						value={searchQuery}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
					/>
				</div>
				<div className="w-48">
					<Select value={dateRange} onChange={handleDateRangeChange}>
						<option value="">All Time</option>
						<option value="today">Today</option>
						<option value="7d">Last 7 Days</option>
						<option value="30d">Last 30 Days</option>
					</Select>
				</div>
			</div>

			{error && (
				<div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
					{error}
				</div>
			)}

			{loading ? (
				<div className="py-12 text-center text-zinc-500">Loading orders...</div>
			) : filteredOrders.length === 0 ? (
				<div className="py-12 text-center text-zinc-500">
					{searchQuery ? 'No orders match your search.' : 'No orders found.'}
				</div>
			) : (
				<>
					<Table>
						<TableHead>
							<TableRow>
								<TableHeader>Image</TableHeader>
								<TableHeader>Order ID</TableHeader>
								<TableHeader>User</TableHeader>
								<TableHeader>Recipient</TableHeader>
								<TableHeader>Message</TableHeader>
								<TableHeader>Status</TableHeader>
								<TableHeader>Payment</TableHeader>
								<TableHeader>Fulfillment</TableHeader>
								<TableHeader>Created</TableHeader>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredOrders.map((order) => (
								<TableRow key={order.id} href={`/orders/${order.id}`}>
									<TableCell>
										{(() => {
											const frontAsset = (order as any).assets?.find((a: any) => a.asset_type === 'USER_UPLOAD');
											return frontAsset ? (
									<ImageThumbnail assetId={frontAsset.id} alt="Front" height={40} />
											) : (
												<span className="text-zinc-400">—</span>
											);
										})()}
									</TableCell>
									<TableCell>
										<ResourceLink id={order.id} />
									</TableCell>
									<TableCell>
										{order.users?.display_name || order.users?.phone_number || '—'}
									</TableCell>
									<TableCell>
										{getSlotText(order.order_slots ?? [], 'recipient_name')}
									</TableCell>
									<TableCell className="max-w-[200px] truncate text-zinc-500">
										{(() => {
											const text = getSlotText(order.order_slots ?? [], 'message_text');
											return text.length > 40 ? text.substring(0, 40) + '…' : text;
										})()}
									</TableCell>
								<TableCell>
									<OrderStatusBadge status={order.status} />
								</TableCell>
								<TableCell>
									{order.payment_intents && order.payment_intents.length > 0
										? <PaymentStatusBadge status={order.payment_intents[0].status as PaymentStatus} />
										: <span className="text-zinc-400">—</span>}
								</TableCell>
								<TableCell>
									{order.prodigi_orders && order.prodigi_orders.length > 0
										? <ProdigiStatusBadge status={order.prodigi_orders[0].status as ProdigiStatus} />
										: order.lob_orders && order.lob_orders.length > 0
											? <LobStatusBadge status={order.lob_orders[0].status as LobStatus} />
											: <span className="text-zinc-400">—</span>}
								</TableCell>
									<TableCell className="text-zinc-500">
										{new Date(order.created_at).toLocaleDateString('en-US', {
											month: 'short',
											day: 'numeric',
											year: 'numeric',
											hour: '2-digit',
											minute: '2-digit',
										})}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>

					<div className="mt-4 flex items-center justify-between">
						<p className="text-sm text-zinc-500">
							Showing {showingStart}–{showingEnd} of {totalCount}
						</p>
						<div className="flex gap-2">
							<Button
								plain
								disabled={offset === 0}
								onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
							>
								Previous
							</Button>
							<Button
								plain
								disabled={offset + PAGE_SIZE >= totalCount}
								onClick={() => setOffset(offset + PAGE_SIZE)}
							>
								Next
							</Button>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
